/**
 * concertAudio.ts — (#8) plays the concert's real FFR track in the browser with DISTANCE-attenuated
 * volume: the further the concert grounds are dragged from screen-centre, the quieter it gets. Web Audio
 * (a one-shot buffer source → a GainNode), unlocked on the first user gesture (browser autoplay policy).
 * The featured song plays ONCE per show: each show carries a `session` id, and once the track has
 * finished (or been started) within a session it will not restart until a NEW session arrives. The
 * scene drives it each frame: play(url, session) while live, setVolume(distance-gain), stop() on end/scroll.
 */
class ConcertAudio {
    private ctx: AudioContext | null = null;
    private gain: GainNode | null = null;
    private src: AudioBufferSourceNode | null = null;
    private analyser: AnalyserNode | null = null;   // (#concertfx) pre-volume amplitude tap for the light show
    private fftBuf: Uint8Array<ArrayBuffer> | null = null;
    private url = '';
    private loading = '';
    private session = -1;      // (#8) the show this audio belongs to; a new session re-arms play-once
    private spent = false;     // this session's song has already started/finished → don't replay
    unlocked = false;

    /** create the AudioContext on a real user gesture (click/key) so the browser lets it make sound. */
    unlock(): void {
        if (this.unlocked) return;
        try {
            const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
            this.ctx = new AC(); this.gain = this.ctx.createGain(); this.gain.gain.value = 0; this.gain.connect(this.ctx.destination);
            // (#concertfx) sources route through the analyser BEFORE the distance-volume gain, so
            // the light show reads the music's true energy even when the stage is far/quiet
            this.analyser = this.ctx.createAnalyser(); this.analyser.fftSize = 256; this.analyser.smoothingTimeConstant = 0.55;
            this.analyser.connect(this.gain);
            this.fftBuf = new Uint8Array(this.analyser.frequencyBinCount);
            this.unlocked = true;
        } catch { /* no Web Audio */ }
    }
    async play(url: string, session: number): Promise<void> {
        if (!this.unlocked || !this.ctx || !this.gain || !url) return;
        if (session !== this.session) { this.session = session; this.spent = false; this.stop(); }  // new show → re-arm
        if (this.ctx.state === 'suspended') { try { await this.ctx.resume(); } catch { /* */ } }
        if (this.spent) return;                         // this show's song already played (once) → stay quiet
        if (url === this.url && this.src) return;       // already spinning this track
        if (url === this.loading) return;               // fetch in flight
        this.loading = url;
        try {
            const r = await fetch(url); if (!r.ok) { this.loading = ''; return; }
            const ab = await r.arrayBuffer();
            const buf = await this.ctx.decodeAudioData(ab);
            if (this.loading !== url || this.spent) return;   // a newer track / show changed while decoding
            this.stop();
            const s = this.ctx.createBufferSource(); s.buffer = buf; s.loop = false; s.connect(this.analyser ?? this.gain);
            s.onended = () => { if (this.src === s) { this.src = null; this.url = ''; } };   // song finished → don't restart
            s.start(); this.spent = true;               // ONE play per show session
            this.src = s; this.url = url;
        } catch { /* fetch/decode failed → silent */ }
        finally { if (this.loading === url) this.loading = ''; }
    }
    stop(): void {
        if (this.src) { try { this.src.stop(); } catch { /* */ } try { this.src.disconnect(); } catch { /* */ } this.src = null; }
        this.url = '';
    }
    /** 0..1 — smoothed so panning doesn't crackle. */
    setVolume(v: number): void {
        if (!this.gain || !this.ctx) return;
        this.gain.gain.setTargetAtTime(Math.max(0, Math.min(1, v)), this.ctx.currentTime, 0.12);
    }
    get playing(): boolean { return !!this.src; }
    /** (#concertfx) 0..1 bass-weighted music energy right now (0 when nothing is playing). */
    level(): number {
        if (!this.analyser || !this.fftBuf || !this.src) return 0;
        this.analyser.getByteFrequencyData(this.fftBuf);
        let sum = 0; const n = Math.min(24, this.fftBuf.length);   // the low bins carry the kick/bass
        for (let i = 0; i < n; i++) sum += this.fftBuf[i]!;
        return Math.min(1, sum / n / 175);
    }
}

export const concertAudio = new ConcertAudio();
